/**
 * Students Table Component
 *
 * Displays student roster in a Material-UI DataGrid with CRUD operations.
 */

import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Upload as UploadIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import {
  useGetStudentsQuery,
  useLazyDownloadTemplateQuery,
} from '../../api/studentsApi';
import type { Student } from '../../types/students';
import StudentFormDialog from './components/StudentFormDialog';
import StudentUploadDialog from './components/StudentUploadDialog';
import DeleteConfirmationDialog from './components/DeleteConfirmationDialog';
import StudentUploadHelp from './components/StudentUploadHelp';

interface StudentsTableProps {
  studioId: string;
}

const StudentsTable: React.FC<StudentsTableProps> = ({ studioId }) => {
  // Fetch students data
  const {
    data: students = [],
    isLoading,
    error,
  } = useGetStudentsQuery(studioId);

  // Dialog state management
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');

  // Pagination state
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 10,
    page: 0,
  });

  // Template download query
  const [downloadTemplate] = useLazyDownloadTemplateQuery();

  // Action handlers
  const handleAddStudent = () => {
    setFormMode('create');
    setSelectedStudent(null);
    setFormDialogOpen(true);
  };

  const handleEditStudent = (student: Student) => {
    setFormMode('edit');
    setSelectedStudent(student);
    setFormDialogOpen(true);
  };

  const handleDeleteStudent = (student: Student) => {
    setSelectedStudent(student);
    setDeleteDialogOpen(true);
  };

  const handleUploadExcel = () => {
    setUploadDialogOpen(true);
  };

  const handleDownloadTemplate = async () => {
    try {
      const result = await downloadTemplate().unwrap();
      // Create download link
      const url = window.URL.createObjectURL(result);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'student_roster_template.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download template:', err);
    }
  };

  // DataGrid columns
  const columns: GridColDef[] = [
    {
      field: 'full_name',
      headerName: 'Name',
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'location',
      headerName: 'Location',
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'phone',
      headerName: 'Phone',
      width: 130,
    },
    {
      field: 'gender',
      headerName: 'Gender',
      width: 80,
    },
    {
      field: 'age',
      headerName: 'Age',
      width: 70,
    },
    {
      field: 'birthday',
      headerName: 'Birthday',
      width: 120,
    },
    {
      field: 'classes',
      headerName: 'Classes',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (params: GridRenderCellParams<Student>) => (
        <Box>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={() => handleEditStudent(params.row)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              color="error"
              onClick={() => handleDeleteStudent(params.row)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  // Render content based on state
  let content;

  if (isLoading) {
    content = (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  } else if (error) {
    content = (
      <Alert severity="error">
        Failed to load students. Please try again later.
      </Alert>
    );
  } else if (students.length === 0) {
    content = (
      <Box textAlign="center" py={6}>
        <Typography variant="h6" gutterBottom>
          No Students Yet
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={3}>
          Add students individually or upload an Excel file to get started
        </Typography>
        <Box display="flex" gap={2} justifyContent="center">
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddStudent}
          >
            Add Student
          </Button>
          <Button
            variant="outlined"
            startIcon={<UploadIcon />}
            onClick={handleUploadExcel}
          >
            Upload Excel
          </Button>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadTemplate}
          >
            Download Template
          </Button>
        </Box>
      </Box>
    );
  } else {
    content = (
      <>
        {/* Action buttons */}
        <Box display="flex" gap={2} mb={2} justifyContent="flex-end">
          <Button
            variant="outlined"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadTemplate}
          >
            Download Template
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<UploadIcon />}
            onClick={handleUploadExcel}
          >
            Upload Excel
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleAddStudent}
          >
            Add Student
          </Button>
        </Box>

        {/* Data grid */}
        <DataGrid
          rows={students}
          columns={columns}
          paginationMode="client"
          pagination
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[5, 10, 25, 50, 100]}
          disableRowSelectionOnClick
          autoHeight
          sx={{
            '& .MuiDataGrid-footerContainer': {
              minHeight: '52px',
            },
          }}
        />
      </>
    );
  }

  return (
    <>
      {/* Help Section - Always visible */}
      <StudentUploadHelp />

      {content}

      {/* Dialogs - Always rendered so they're available in all states */}
      <StudentFormDialog
        open={formDialogOpen}
        onClose={() => setFormDialogOpen(false)}
        studioId={studioId}
        student={selectedStudent}
        mode={formMode}
      />

      <StudentUploadDialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        studioId={studioId}
      />

      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        studioId={studioId}
        student={selectedStudent}
      />
    </>
  );
};

export default StudentsTable;
